import { useEffect, useRef, useState } from 'react';

// The build this SPA bundle was compiled from — baked by Vite at build time
// from the Dockerfile's VITE_BUILD_ID (the ECR image tag). 'dev' for local or
// un-tagged builds.
export const CLIENT_BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? 'dev';

interface VersionResp {
  buildId: string;
}

/**
 * Polls GET /api/version and returns the server's build id once it differs from
 * the one THIS tab was compiled with — i.e. a newer image was deployed and this
 * tab is now stale. Returns null until a real, different build is observed.
 *
 * Guards:
 *  - never nags when either side is 'dev' (local, or an image built without the
 *    build-arg) → no false prompts in dev, safe degradation in prod.
 *  - checks on an interval AND whenever the tab regains focus (returning to a
 *    long-idle tab is exactly when a deploy has most likely happened).
 *  - stops once a newer build is seen (the answer won't change until reload).
 *  - swallows fetch errors so a transient blip never fabricates a nudge.
 */
export function useNewBuildAvailable(pollMs = 90_000): string | null {
  const [newBuild, setNewBuild] = useState<string | null>(null);
  const stopped = useRef(false);

  useEffect(() => {
    if (CLIENT_BUILD_ID === 'dev') return; // local dev — never nag
    let timer: number | undefined;

    const check = async () => {
      if (stopped.current) return;
      try {
        const res = await fetch('/api/version', { credentials: 'include', cache: 'no-store' });
        if (!res.ok) return;
        const { buildId } = (await res.json()) as VersionResp;
        if (buildId && buildId !== 'dev' && buildId !== CLIENT_BUILD_ID) {
          stopped.current = true;
          setNewBuild(buildId);
          if (timer) window.clearInterval(timer);
        }
      } catch {
        /* transient — ignore */
      }
    };

    timer = window.setInterval(check, pollMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);
    void check(); // check once on mount

    return () => {
      if (timer) window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [pollMs]);

  return newBuild;
}

// Never reload out from under someone mid-input: if a text field / editable is
// focused, they're typing (search, scoring config, a tag, an assign menu) — hold
// off until they're done. A backgrounded/idle tab with nothing focused is safe.
function isSafeToReload(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return true;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  return !el.isContentEditable;
}

// Reload at most once per detected build. Marking BEFORE reload means that if the
// image somehow serves a client id that still mismatches (a misbuilt --build-arg),
// we detect it again post-reload but don't loop — we just fall back to the manual
// banner. A genuinely newer build later has a new id → auto-reloads again.
function reloadOnceFor(buildId: string): void {
  const KEY = 'funnel:auto-reloaded-to';
  try {
    if (sessionStorage.getItem(KEY) === buildId) return;
    sessionStorage.setItem(KEY, buildId);
  } catch {
    /* private mode / storage disabled — the in-page dedupe below still holds */
  }
  // index.html is served no-cache and assets are content-hashed + immutable, so
  // a normal reload reliably pulls the new bundle (no forced/hard reload needed).
  window.location.reload();
}

/**
 * Automatically pull a newly-deployed build into this tab — but only at a SAFE
 * moment, so nobody loses work or gets yanked mid-read:
 *   - the tab goes hidden (they switched away → reload invisibly), or
 *   - they've been idle for `idleMs` with no field focused.
 * A no-op until `newBuild` is set. The visible banner + its "Refresh now" button
 * (UpdateBanner) stay as the manual path for someone parked on one screen.
 */
export function useAutoReload(newBuild: string | null, idleMs = 60_000): void {
  useEffect(() => {
    if (!newBuild) return;
    let idleTimer: number | undefined;

    const tryReload = () => {
      if (isSafeToReload()) reloadOnceFor(newBuild);
      else armIdle(); // still typing — check again after another idle span
    };
    const armIdle = () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(tryReload, idleMs);
    };
    const onActivity = () => armIdle();
    const onVisibility = () => {
      // Backgrounded with nothing focused → the safest possible moment.
      if (document.visibilityState === 'hidden' && isSafeToReload()) reloadOnceFor(newBuild);
    };

    const ACTIVITY = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;
    ACTIVITY.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    armIdle();

    return () => {
      if (idleTimer) window.clearTimeout(idleTimer);
      ACTIVITY.forEach((e) => window.removeEventListener(e, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [newBuild, idleMs]);
}
